import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import Select from "react-select";
import { Bounce, ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Card, CardBody, Col, Row } from "reactstrap";
import * as XLSX from "xlsx";
import api from "../../services/api";
import { setBreadcrumbItems } from "../../store/actions";


const BulkStudentRegistration = (props) => {
  document.title = "Technical Hub | Bulk Student Registration";

  const breadcrumbItems = [
    { title: "Technical Hub", link: "#" },
    { title: "Student Registrations", link: "#" },
    { title: "Bulk Student Registration", link: "#" },
  ];

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const fileInputRef = useRef(null);

  const [courses, setCourses] = useState([]);
  const [programNames, setProgramNames] = useState([]);
  const [technologies, setTechnologies] = useState([]);

  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedTechnology, setSelectedTechnology] = useState(null);

  const [addedBy, setAddedBy] = useState("admin");
  const [excelFile, setExcelFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [rollCount, setRollCount] = useState(0);

  useEffect(() => {
    const curr_user = JSON.parse(localStorage.getItem("authUser"));
    console.log("[AUTH] Current user from localStorage:", curr_user);
    if (curr_user) setAddedBy(curr_user.first_name);
    fetchCourses();
    props.setBreadcrumbItems("Bulk Student Registration", breadcrumbItems);
  }, []);

  const fetchCourses = async () => {
    console.log("[COURSES] Fetching from:", `${backendUrl}get-courses`);
    try {
      const { data } = await axios.get(`${backendUrl}get-courses`);
      console.log("[COURSES] Response:", data);
      setCourses(data || []);
    } catch (error) {
      console.error("[COURSES] Error:", error.response || error);
      toast.error("Failed to load courses.");
    }
  };

  useEffect(() => {
    if (!selectedCourse) {
      setProgramNames([]);
      setTechnologies([]);
      setSelectedProgram(null);
      setSelectedTechnology(null);
      return;
    }
    console.log("[PROGRAMS] Selected course:", selectedCourse);
    const fetchPrograms = async () => {
      const url = `${backendUrl}get-programs-by-course-id/${selectedCourse.value}`;
      console.log("[PROGRAMS] Fetching from:", url);
      try {
        const response = await axios.get(url);
        console.log("[PROGRAMS] Response:", response.data);
        setProgramNames(response.data.coursePrograms || []);
      } catch (error) {
        console.error("[PROGRAMS] Error:", error.response || error);
        toast.error("Failed to load programs.");
      }
    };
    fetchPrograms();
  }, [selectedCourse]);

  useEffect(() => {
    if (!selectedProgram) {
      setTechnologies([]);
      setSelectedTechnology(null);
      return;
    }
    console.log("[TECHNOLOGIES] Selected program:", selectedProgram);
    const fetchTechnologies = async () => {
      const url = `${backendUrl}get-technology-by-program/${selectedProgram.value}`;
      console.log("[TECHNOLOGIES] Fetching from:", url);
      try {
        const response = await axios.get(url);
        console.log("[TECHNOLOGIES] Response:", response.data);
        setTechnologies(response.data.Technology || []);
      } catch (error) {
        console.error("[TECHNOLOGIES] Error:", error.response || error);
        toast.error("Failed to load technologies.");
      }
    };
    fetchTechnologies();
  }, [selectedProgram]);

  const parseExcel = (file) => {
    console.log("[EXCEL] Parsing file:", file.name, "| Size:", file.size, "bytes");
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        console.log("[EXCEL] Sheet names:", workbook.SheetNames);

        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet);
        console.log("[EXCEL] Raw rows:", rows);

        const rollNos = rows
          .map((row) =>
            String(row["roll_no"] || row["Roll No"] || row["RollNo"] || "").trim()
          )
          .filter(Boolean);

        console.log("[EXCEL] Extracted roll numbers:", rollNos);
        setRollCount(rollNos.length);

        if (rollNos.length === 0) {
          toast.warning("No roll numbers found. Ensure column is named 'roll_no'");
        } else {
          toast.success(`${rollNos.length} roll number(s) loaded.`);
        }
      } catch (err) {
        console.error("[EXCEL] Parse error:", err);
        toast.error("Failed to parse Excel file.");
      }
    };
    reader.onerror = (err) => {
      console.error("[EXCEL] FileReader error:", err);
      toast.error("Failed to read file.");
    };
    reader.readAsArrayBuffer(file);
  };

  const handleFileChange = (file) => {
    if (!file) return;
    console.log("[FILE] Selected:", file.name, file.type, file.size);
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["xlsx", "xls"].includes(ext)) {
      console.warn("[FILE] Invalid type:", ext);
      toast.error("Only .xlsx or .xls files are allowed.");
      return;
    }
    setExcelFile(file);
    setResult(null);
    parseExcel(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    console.log("[DROP] Dropped file:", file?.name);
    handleFileChange(file);
  };

  const handleRemoveFile = () => {
    console.log("[FILE] Removed");
    setExcelFile(null);
    setRollCount(0);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("[SUBMIT] Form submitted");

    if (!excelFile) { toast.error("Please upload an Excel file."); return; }
    if (!selectedCourse) { toast.error("Please select a course."); return; }
    if (!selectedProgram) { toast.error("Please select a program."); return; }
    if (!selectedTechnology) { toast.error("Please select a technology."); return; }
    if (rollCount === 0) { toast.error("No valid roll numbers found in Excel."); return; }

    console.log("[SUBMIT] reg_id:", selectedTechnology.reg_id);
    console.log("[SUBMIT] course:", selectedCourse.value);
    console.log("[SUBMIT] technology:", selectedTechnology.value);
    console.log("[SUBMIT] added_by:", addedBy);
    console.log("[SUBMIT] file:", excelFile.name, excelFile.size, "bytes");

    const formData = new FormData();
    formData.append("file", excelFile);
    formData.append("reg_id", selectedTechnology.reg_id);
    formData.append("course", selectedCourse.value);
    formData.append("technology", selectedTechnology.value);
    formData.append("added_by", addedBy);

    console.log("[FORMDATA] Entries:");
    for (let [key, value] of formData.entries()) {
      console.log(`  ${key}:`, value);
    }

    const url = `${backendUrl}bulk-register-students-from-excel`;
    console.log("[SUBMIT] POST URL:", url);

    setIsSubmitting(true);
    setResult(null);

    try {
      const res = await api.post(url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      console.log("[SUBMIT] Response status:", res.status);
      console.log("[SUBMIT] Response data:", res.data);

      if (res.status === 201) {
        setResult(res.data);
        toast.success("Bulk registration completed!");
      }
    } catch (error) {
      console.error("[SUBMIT] Error:", error);
      console.error("[SUBMIT] Status:", error.response?.status);
      console.error("[SUBMIT] Response data:", error.response?.data);
      console.error("[SUBMIT] Message:", error.message);
      const msg = error.response?.data?.message || "Bulk registration failed.";
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    console.log("[RESET] Form reset");
    setSelectedCourse(null);
    setSelectedProgram(null);
    setSelectedTechnology(null);
    setExcelFile(null);
    setRollCount(0);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderRadius: "8px",
      border: state.isFocused ? "1.5px solid #4f46e5" : "1.5px solid #e2e8f0",
      boxShadow: state.isFocused ? "0 0 0 3px rgba(79,70,229,0.1)" : "none",
      minHeight: "42px",
      fontSize: "14px",
      transition: "all 0.2s",
      "&:hover": { borderColor: "#4f46e5" },
    }),
    option: (base, state) => ({
      ...base,
      fontSize: "14px",
      backgroundColor: state.isSelected ? "#4f46e5" : state.isFocused ? "#eef2ff" : "white",
      color: state.isSelected ? "white" : "#1e293b",
    }),
    placeholder: (base) => ({ ...base, color: "#94a3b8", fontSize: "14px" }),
  };

  return (
    <React.Fragment>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700&display=swap');

        .bulk-reg-wrapper * { font-family: 'Sora', sans-serif; }

        .bulk-reg-card {
          border: none;
          border-radius: 16px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 4px 24px rgba(79,70,229,0.06);
          overflow: hidden;
        }

        .bulk-reg-header {
          background: linear-gradient(135deg, #4f46e5 0%, #6366f1 50%, #818cf8 100%);
          padding: 28px 32px;
          position: relative;
          overflow: hidden;
        }

        .bulk-reg-header::before {
          content: '';
          position: absolute;
          top: -40px; right: -40px;
          width: 160px; height: 160px;
          border-radius: 50%;
          background: rgba(255,255,255,0.08);
        }

        .bulk-reg-header h4 {
          color: white;
          font-size: 22px;
          font-weight: 700;
          margin: 0 0 6px;
          position: relative; z-index: 1;
        }

        .bulk-reg-header p {
          color: rgba(255,255,255,0.75);
          font-size: 13.5px;
          margin: 0;
          position: relative; z-index: 1;
        }

        .bulk-reg-body { padding: 32px; }

        .form-label-styled {
          font-size: 13px;
          font-weight: 600;
          color: #374151;
          margin-bottom: 6px;
          display: block;
        }

        .drop-zone {
          border: 2px dashed #c7d2fe;
          border-radius: 12px;
          background: #fafbff;
          padding: 40px 24px;
          text-align: center;
          cursor: pointer;
          transition: all 0.25s ease;
        }

        .drop-zone:hover, .drop-zone.dragging {
          border-color: #4f46e5;
          background: #eef2ff;
        }

        .dz-icon {
          width: 52px; height: 52px;
          background: linear-gradient(135deg, #eef2ff, #e0e7ff);
          border-radius: 14px;
          display: flex; align-items: center; justify-content: center;
          margin: 0 auto 14px;
          font-size: 22px;
        }

        .dz-title {
          font-size: 15px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 4px;
        }

        .dz-sub { font-size: 12.5px; color: #94a3b8; }

        .dz-browse {
          color: #4f46e5;
          font-weight: 600;
          text-decoration: underline;
          text-underline-offset: 2px;
        }

        .file-attached {
          background: linear-gradient(135deg, #f0fdf4, #dcfce7);
          border: 1.5px solid #86efac;
          border-radius: 12px;
          padding: 16px 20px;
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .file-icon {
          width: 40px; height: 40px;
          background: #22c55e;
          border-radius: 10px;
          display: flex; align-items: center; justify-content: center;
          font-size: 18px; color: white; flex-shrink: 0;
        }

        .file-name {
          font-size: 14px; font-weight: 600; color: #15803d;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 300px;
        }

        .file-count { font-size: 12px; color: #16a34a; margin-top: 2px; }

        .remove-btn {
          margin-left: auto;
          background: none; border: none;
          color: #dc2626; font-size: 18px; cursor: pointer;
          padding: 4px 8px; border-radius: 6px; transition: background 0.2s; flex-shrink: 0;
        }

        .remove-btn:hover { background: #fee2e2; }

        .divider { height: 1px; background: #f1f5f9; margin: 24px 0; }

        .result-box {
          border-radius: 12px; overflow: hidden;
          border: 1px solid #e2e8f0; margin-top: 24px;
        }

        .result-header { background: #1e293b; padding: 14px 20px; }
        .result-header span { color: white; font-size: 14px; font-weight: 600; }

        .result-stats {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          background: white;
        }

        .stat-item {
          padding: 20px 16px; text-align: center;
          border-right: 1px solid #f1f5f9;
        }

        .stat-item:last-child { border-right: none; }

        .stat-num { font-size: 28px; font-weight: 700; line-height: 1; margin-bottom: 4px; }
        .stat-label { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.8px; color: #94a3b8; }

        .stat-total .stat-num { color: #1e293b; }
        .stat-success .stat-num { color: #16a34a; }
        .stat-skip .stat-num { color: #d97706; }
        .stat-fail .stat-num { color: #dc2626; }

        .submit-btn {
          background: linear-gradient(135deg, #4f46e5, #6366f1) !important;
          border: none !important; border-radius: 10px !important;
          padding: 11px 28px !important; font-size: 14px !important;
          font-weight: 600 !important; color: white !important; cursor: pointer;
          transition: all 0.25s ease !important;
          box-shadow: 0 4px 12px rgba(79,70,229,0.3) !important;
        }

        .submit-btn:hover:not(:disabled) {
          transform: translateY(-1px) !important;
          box-shadow: 0 6px 18px rgba(79,70,229,0.4) !important;
        }

        .submit-btn:disabled { opacity: 0.65 !important; cursor: not-allowed !important; }

        .reset-btn {
          background: white !important; border: 1.5px solid #e2e8f0 !important;
          border-radius: 10px !important; padding: 11px 22px !important;
          font-size: 14px !important; font-weight: 600 !important;
          color: #64748b !important; cursor: pointer; transition: all 0.2s !important;
        }

        .reset-btn:hover { border-color: #94a3b8 !important; color: #1e293b !important; }

        .spinner {
          display: inline-block; width: 14px; height: 14px;
          border: 2px solid rgba(255,255,255,0.4); border-top-color: white;
          border-radius: 50%; animation: spin 0.7s linear infinite;
          margin-right: 8px; vertical-align: middle;
        }

        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      <div className="bulk-reg-wrapper">
        <ToastContainer position="top-right" autoClose={4000} hideProgressBar={false} transition={Bounce} theme="light" />

        <Row>
          <Col xl="12">
            <Card className="bulk-reg-card">
              <div className="bulk-reg-header">
                <h4>Bulk Student Registration</h4>
                <p>Upload an Excel sheet with roll numbers and assign course, program &amp; technology.</p>
              </div>

              <CardBody className="bulk-reg-body">
                <form onSubmit={handleSubmit}>

                  {/* Upload Excel */}
                  <label className="form-label-styled">
                    Upload Excel File <span style={{ color: "#ef4444" }}>*</span>
                  </label>

                  {!excelFile ? (
                    <div
                      className={`drop-zone ${isDragging ? "dragging" : ""}`}
                      onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                      onDragLeave={() => setIsDragging(false)}
                      onDrop={handleDrop}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <div className="dz-icon">📊</div>
                      <div className="dz-title">Drop your Excel file here</div>
                      <div className="dz-sub">
                        or <span className="dz-browse">browse to upload</span> &nbsp;·&nbsp; .xlsx / .xls only
                      </div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx,.xls"
                        style={{ display: "none" }}
                        onChange={(e) => handleFileChange(e.target.files[0])}
                      />
                    </div>
                  ) : (
                    <div className="file-attached">
                      <div className="file-icon">✓</div>
                      <div style={{ flex: 1, overflow: "hidden" }}>
                        <div className="file-name">{excelFile.name}</div>
                        <div className="file-count">{rollCount} roll number(s) detected</div>
                      </div>
                      <button type="button" className="remove-btn" onClick={handleRemoveFile}>✕</button>
                    </div>
                  )}

                  <div className="divider" />

                  {/* Selects */}
                  <Row>
                    <Col md="4">
                      <div className="mb-4">
                        <label className="form-label-styled">
                          Course <span style={{ color: "#ef4444" }}>*</span>
                        </label>
                        <Select
                          value={selectedCourse}
                          onChange={(course) => {
                            console.log("[SELECT] Course:", course);
                            setSelectedCourse(course);
                            setSelectedProgram(null);
                            setSelectedTechnology(null);
                            setProgramNames([]);
                            setTechnologies([]);
                          }}
                          options={courses.map((c) => ({ value: c._id, label: c.course_name }))}
                          placeholder="Choose Course"
                          styles={customSelectStyles}
                        />
                      </div>
                    </Col>

                    <Col md="4">
                      <div className="mb-4">
                        <label className="form-label-styled">
                          Program <span style={{ color: "#ef4444" }}>*</span>
                        </label>
                        <Select
                          value={selectedProgram}
                          onChange={(program) => {
                            console.log("[SELECT] Program:", program);
                            setSelectedProgram(program);
                            setSelectedTechnology(null);
                            setTechnologies([]);
                          }}
                          options={programNames.map((p) => ({ value: p.program_name, label: p.program_name }))}
                          isDisabled={!selectedCourse}
                          placeholder={selectedCourse ? "Choose Program" : "Select course first"}
                          styles={customSelectStyles}
                        />
                      </div>
                    </Col>

                    <Col md="4">
                      <div className="mb-4">
                        <label className="form-label-styled">
                          Technology <span style={{ color: "#ef4444" }}>*</span>
                        </label>
                        <Select
                          value={selectedTechnology}
                          onChange={(tech) => {
                            console.log("[SELECT] Technology:", tech);
                            setSelectedTechnology(tech);
                          }}
                          options={technologies.map((t) => ({
                            value: t.technology_id,
                            label: t.technology_name,
                            reg_id: t._id,
                          }))}
                          isDisabled={!selectedProgram}
                          placeholder={selectedProgram ? "Choose Technology" : "Select program first"}
                          styles={customSelectStyles}
                        />
                      </div>
                    </Col>
                  </Row>

                  <div className="divider" />

                  {/* Actions */}
                  <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                    <button
                      type="submit"
                      className="submit-btn"
                      disabled={isSubmitting || rollCount === 0 || !selectedTechnology}
                    >
                      {isSubmitting ? <><span className="spinner" />Processing...</> : "Register Students"}
                    </button>

                    <button type="button" className="reset-btn" onClick={handleReset}>
                      ↺ Reset
                    </button>

                    {rollCount > 0 && (
                      <span style={{ fontSize: "13px", color: "#64748b" }}>
                        {rollCount} student(s) queued
                      </span>
                    )}
                  </div>

                  {/* Result */}
                  {result && (
                    <div className="result-box">
                      <div className="result-header">
                        <span>✅ Bulk Registration Completed</span>
                      </div>
                      <div className="result-stats">
                        <div className="stat-item stat-total">
                          <div className="stat-num">{result.summary?.total_in_excel ?? 0}</div>
                          <div className="stat-label">Total</div>
                        </div>
                        <div className="stat-item stat-success">
                          <div className="stat-num">{result.summary?.registered ?? 0}</div>
                          <div className="stat-label">Registered</div>
                        </div>
                        <div className="stat-item stat-skip">
                          <div className="stat-num">{result.summary?.skipped ?? 0}</div>
                          <div className="stat-label">Skipped</div>
                        </div>
                        <div className="stat-item stat-fail">
                          <div className="stat-num">{result.summary?.failed ?? 0}</div>
                          <div className="stat-label">Failed</div>
                        </div>
                      </div>
                    </div>
                  )}

                </form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </React.Fragment>
  );
};

export default connect(null, { setBreadcrumbItems })(BulkStudentRegistration);
